import { format, formatDistanceToNow, parse } from 'date-fns';
import parsePhoneNumber from 'libphonenumber-js';

/**
 * PETBOOST DATE/TIME FORMATTING STANDARDS
 *
 * This file contains standardized formatting utilities for dates, times, and durations.
 * All components should use these utilities rather than implementing custom formatting logic.
 *
 * DATE FORMATTING STANDARDS:
 * - Use British/Australian formats by default (day/month/year)
 * - Prefer `formatDateWithDayName` for full dates with day names: "Mon, 15 May 2023"
 * - Use `formatDateShort` for compact dates: "15 May 2023"
 * - Use `formatDateHeaderDisplay` for headers: "15 May"
 *
 * TIME FORMATTING STANDARDS:
 * - Use `formatTimeAmPm` for consistent time display: "10:30am"
 * - Use `formatTimeRangeShort` for time ranges: "10:30am - 2:00pm"
 * - Use `formatScheduleDateTime` for full schedule entries: "Mon, 15 May 2023, 10:30am - 2:00pm"
 *
 * API FORMATTING:
 * - Use `formatDateForApi` for API date format: "2023-05-15"
 * - Use `formatTimeForApi` for API time format: "10:30:00"
 *
 * LOCALE CONSISTENCY:
 * - All formatting follows en-AU/en-GB standards
 */

export function formatDate(date: string | Date) {
  return format(date, 'do MMM yyyy'); // ex. 26 Apr 2025
}

export function formatDateAs(date: string | Date, formatStr: string) {
  return format(date, formatStr);
}

export function formatPhoneNumber(phoneNumberStr: string) {
  const phoneNumber = parsePhoneNumber(phoneNumberStr);
  return phoneNumber?.formatInternational() ?? '';
}

/**
 * Detects if a phone number is a mobile number
 * Uses libphonenumber-js for proper international phone number validation
 * Defaults to Australian context if no country code is provided
 *
 * @param phoneNumberStr Phone number string in any format
 * @param defaultCountry Default country code (defaults to 'AU' for Australia)
 * @returns true if the number is a mobile number, false otherwise
 */
export function isMobileNumber(
  phoneNumberStr: string,
  defaultCountry: string = 'AU'
): boolean {
  if (!phoneNumberStr) return false;

  try {
    const phoneNumber = parsePhoneNumber(phoneNumberStr, defaultCountry as any);
    return phoneNumber?.getType() === 'MOBILE';
  } catch {
    // If parsing fails, fallback to basic regex for Australian mobile numbers
    if (defaultCountry === 'AU') {
      const cleanPhone = phoneNumberStr.replace(/[\s\-()]/g, '');
      return /^(\+614|04)\d{8}$/.test(cleanPhone);
    }
    return false;
  }
}

/**
 * Formats a standardized contact message for pending appointments
 * Used for SMS and email templates when contacting businesses about pending bookings
 *
 * @param businessName Name of the business
 * @param serviceName Name of the service booked
 * @param petName Name of the pet
 * @param startDate Formatted start date string (e.g., "Wed 2 Jul 2025")
 * @param appointmentId Appointment ID for generating the link
 * @returns Formatted contact message with business appointment link
 */
export function formatContactMessage(
  businessName: string,
  serviceName: string,
  petName: string,
  startDate: string,
  appointmentId: string
): string {
  const appointmentUrl = `business.petboost.com/appointments/${appointmentId}`;

  return `Hi ${businessName} team, I just booked ${serviceName} with you but see that it is pending. I would like to confirm what further details you need from me to go ahead please?

My dog's name is ${petName} and I booked in on the ${startDate}.

Here is the appointment link:
${appointmentUrl}

*This message was generated by Petboost, please do not edit it and feel free to add any extra details below`;
}

export const formatTime = (time: string, compact: boolean = false): string => {
  // Parse the time string as a date object using a strict pattern
  const parsedDate = parse(time, 'HH:mm:ss', new Date());

  // Check if the parsed date is valid
  if (isNaN(parsedDate.getTime())) {
    return 'Invalid time format';
  }

  // Format the date object to a 12-hour clock with am/pm
  const formattedTime = format(parsedDate, 'h:mm a');

  // If compact mode is requested, remove ':00' and convert to lowercase
  if (compact) {
    return formattedTime.toLowerCase().replace(':00', '');
  }

  return formattedTime;
};

export function formatTimeRange(startTime: Date, endTime: Date): string {
  const startHours = startTime.getHours();
  const startMinutes = startTime.getMinutes();
  const endHours = endTime.getHours();
  const endMinutes = endTime.getMinutes();

  const startPeriod = startHours >= 12 ? 'pm' : 'am';
  const endPeriod = endHours >= 12 ? 'pm' : 'am';

  let startFormatted = startTime
    .toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: startMinutes !== 0 ? '2-digit' : undefined,
      hour12: true,
    })
    .toLowerCase()
    .replace(' ', '');

  const endFormatted = endTime
    .toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: endMinutes !== 0 ? '2-digit' : undefined,
      hour12: true,
    })
    .toLowerCase()
    .replace(' ', '');

  if (startPeriod === endPeriod) {
    startFormatted = startFormatted.replace(/am|pm/, '');
  }

  return `${startFormatted} — ${endFormatted}`;
}

export function toPascalCase(str: string): string {
  return str.replace(/(\w)(\w*)/g, (_, g1, g2) => g1.toUpperCase() + g2);
}

export function convertKeysToPascalCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((v) => convertKeysToPascalCase(v));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const pascalKey = toPascalCase(key);
      result[pascalKey] = convertKeysToPascalCase(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
}

export function formatDateOnly(
  date: string | Date | undefined
): string | undefined {
  // format 'yyyy-MM-dd'
  if (!date) return undefined;
  return format(date, 'yyyy-MM-dd');
}

export const getOrdinalSuffix = (num: number) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

type Frequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';

interface FormatRecurrenceOptions {
  singular: string;
  plural: string;
}

export function formatRecurrencePattern(
  pattern: string,
  startDate: Date | string | null | undefined,
  options: FormatRecurrenceOptions
): string {
  try {
    // Ensure startDate is a valid Date object
    let validStartDate: Date;

    if (startDate instanceof Date) {
      validStartDate = startDate;
    } else if (startDate) {
      // Try to create a date from the string
      const parsedDate = new Date(startDate);

      // Check if the parsed date is valid
      if (!isNaN(parsedDate.getTime())) {
        validStartDate = parsedDate;
      } else {
        // Invalid startDate string - use fallback
        validStartDate = new Date();
      }
    } else {
      // Handle null/undefined case
      validStartDate = new Date();
    }

    // Parse the pattern
    const params = Object.fromEntries(
      pattern.split(';').map((param) => param.split('='))
    );

    const freq = params['FREQ'] as Frequency;
    if (!freq) {
      return 'Invalid recurrence pattern';
    }

    // Get frequency text and capitalize first letter
    const freqText = freq.charAt(0).toUpperCase() + freq.slice(1).toLowerCase();

    // Handle WEEKLY with BYDAY first (before COUNT) - this covers cases like "FREQ=WEEKLY;BYDAY=FR;COUNT=4"
    if (freq === 'WEEKLY' && params['BYDAY']) {
      const days = params['BYDAY'].split(',');
      const dayNames: Record<string, string> = {
        MO: 'Mon',
        TU: 'Tue',
        WE: 'Wed',
        TH: 'Thu',
        FR: 'Fri',
        SA: 'Sat',
        SU: 'Sun',
      };

      const count = parseInt(params['COUNT'] || '0');

      if (days.length === 1) {
        const dayName = dayNames[days[0]] || days[0];

        // If there's a count, include it in the description
        if (count > 0) {
          const noun = count === 1 ? options.singular : options.plural;
          return `${count} ${noun.charAt(0).toUpperCase() + noun.slice(1)} ${freqText} on ${dayName}`;
        }

        return `${freqText} on ${dayName}`;
      } else if (days.length > 0) {
        const validDays = days
          .filter((day: string) => day)
          .map((day: string) => dayNames[day] || day);

        // Format with commas and "and" before the last item
        let formattedDays: string;
        if (validDays.length === 2) {
          formattedDays = validDays.join(' and ');
        } else {
          // For 3+ days: join all but last with commas, then add "and" before the last
          const lastDay = validDays.pop();
          formattedDays = validDays.join(', ') + ' and ' + lastDay;
        }

        // If there's a count, include it in the description
        if (count > 0) {
          const noun = count === 1 ? options.singular : options.plural;
          return `${count} ${noun.charAt(0).toUpperCase() + noun.slice(1)} ${freqText} on ${formattedDays}`;
        }

        return `${freqText} on ${formattedDays}`;
      }
    }

    // Handle patterns with COUNT (for non-WEEKLY with BYDAY cases)
    const count = parseInt(params['COUNT'] || '0');
    if (count > 0) {
      // Additional safety check before calling format
      if (isNaN(validStartDate.getTime())) {
        // Invalid validStartDate - use fallback
        validStartDate = new Date();
      }

      // Get day of week - abbreviated format
      const dayName = format(validStartDate, 'EEE');

      // Determine singular or plural noun
      const noun = count === 1 ? options.singular : options.plural;

      // Construct the readable string with Title Case for each word
      return `${count} ${noun.charAt(0).toUpperCase() + noun.slice(1)} ${freqText} on ${dayName}`;
    }

    // Handle INTERVAL
    const interval = parseInt(params['INTERVAL'] || '0');
    if (interval > 1) {
      // Helper function to get correct singular/plural unit form
      const getUnitText = (count: number, frequency: string): string => {
        if (count === 1) {
          switch (frequency.toLowerCase()) {
            case 'daily':
              return 'day';
            case 'weekly':
              return 'week';
            case 'monthly':
              return 'month';
            default:
              return frequency.toLowerCase();
          }
        }
        // Plural forms
        switch (frequency.toLowerCase()) {
          case 'daily':
            return 'days';
          case 'weekly':
            return 'weeks';
          case 'monthly':
            return 'months';
          default:
            return frequency.toLowerCase() + 's';
        }
      };

      return `Ev. ${interval} ${getUnitText(interval, freq)}`;
    }

    // Default cases
    if (freq === 'DAILY') {
      return 'Daily';
    } else if (freq === 'WEEKLY') {
      // Additional safety check before calling format
      if (isNaN(validStartDate.getTime())) {
        // Invalid validStartDate - use fallback
        validStartDate = new Date();
      }

      // If no specific BYDAY is provided, use the day from startDate
      const dayName = format(validStartDate, 'EEE'); // Use EEE for short day name
      return `${freqText} on ${dayName}`;
    } else if (freq === 'MONTHLY') {
      // Additional safety check before calling format
      if (isNaN(validStartDate.getTime())) {
        // Invalid validStartDate - use fallback
        validStartDate = new Date();
      }

      // For monthly, show the day of month
      const dayOfMonth = validStartDate.getDate();
      const suffix = getOrdinalSuffix(dayOfMonth);
      return `${freqText} on the ${dayOfMonth}${suffix}`;
    }

    // Fallback for any other frequency
    return freqText;
  } catch {
    // Error parsing recurrence pattern - return fallback
    return 'Recurring';
  }
}

export const formatTimeDisplay = (time?: string | null) => {
  // Default to 10:00 AM if no time provided
  if (!time) return '10:00 AM';

  const timeString = time as string; // Type assertion

  // If already in a readable format like "10:00 AM", return as is
  if (timeString.includes(' ')) return timeString;

  try {
    // Common 24h time formats: "14:30" or "14:30:00"
    const parts = timeString.split(':');

    // Must have at least hours and minutes
    if (parts.length < 2) return timeString;

    const hours = parseInt(parts[0] || '0', 10);
    const minutes = parseInt(parts[1] || '0', 10);

    // Guard against NaN
    if (isNaN(hours) || isNaN(minutes)) return timeString;

    // Format with AM/PM
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${displayHours}:${displayMinutes} ${period}`;
  } catch {
    // Fall back to the original format if parsing fails
    return timeString;
  }
};

// Format date for API
export function formatDateForApi(date: Date): string {
  // Create a date-only string in YYYY-MM-DD format
  // This avoids timezone issues by working with local date components only
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Standardizes a TimeSpan format string to ensure it has proper formatting with seconds
 * Handles both regular time format (HH:MM or HH:MM:SS) and day format (d.HH:MM or d.HH:MM:SS)
 *
 * @param timeValue The time string to standardize
 * @returns A properly formatted TimeSpan string
 */
export function timeSpanFormat(timeValue: string): string {
  if (!timeValue) return '00:00:00';

  // Handle day format (d.HH:MM:SS)
  if (timeValue.includes('.')) {
    const parts = timeValue.split('.');
    const days = parts[0] || '0';
    const timeComponent = parts[1] || '';

    // Handle the time component part
    let formattedTime = '';

    if (!timeComponent.includes(':')) {
      // No colons, just a number for hours
      formattedTime = `${timeComponent.padStart(2, '0')}:00:00`;
    } else {
      // Contains colons, parse as HH:MM:SS
      const timeParts = timeComponent.split(':');

      if (timeParts.length === 1) {
        // Just HH:
        formattedTime = `${(timeParts[0] || '00').padStart(2, '0')}:00:00`;
      } else if (timeParts.length === 2) {
        // HH:MM
        const hour = (timeParts[0] || '00').padStart(2, '0');
        const minute = (timeParts[1] || '00').padStart(2, '0');
        formattedTime = `${hour}:${minute}:00`;
      } else if (timeParts.length >= 3) {
        // HH:MM:SS
        const hour = (timeParts[0] || '00').padStart(2, '0');
        const minute = (timeParts[1] || '00').padStart(2, '0');
        const second = (timeParts[2] || '00').padStart(2, '0');
        formattedTime = `${hour}:${minute}:${second}`;
      }
    }

    return `${days}.${formattedTime}`;
  }

  // Handle regular time format (HH:MM or HH:MM:SS)
  if (!timeValue.includes(':')) {
    // Just a number for hours
    return `${timeValue.padStart(2, '0')}:00:00`;
  } else {
    // Contains colons, parse as HH:MM:SS
    const parts = timeValue.split(':');

    if (parts.length === 1) {
      // Just HH:
      return `${(parts[0] || '00').padStart(2, '0')}:00:00`;
    } else if (parts.length === 2) {
      // HH:MM
      const hour = (parts[0] || '00').padStart(2, '0');
      const minute = (parts[1] || '00').padStart(2, '0');
      return `${hour}:${minute}:00`;
    } else {
      // HH:MM:SS
      const hour = (parts[0] || '00').padStart(2, '0');
      const minute = (parts[1] || '00').padStart(2, '0');
      const second = (parts[2] || '00').padStart(2, '0');
      return `${hour}:${minute}:${second}`;
    }
  }
}

// Update formatTimeForApi to use timeSpanFormat
export function formatTimeForApi(time: string | undefined): string {
  if (!time) return '10:00:00';
  return timeSpanFormat(time);
}

// Calculate the difference in calendar days between two dates
// To ensure we get the correct number of calendar days, we reset the time to midnight
export function getDaysBetweenDates(date1: Date, date2: Date): number {
  // Create new date objects to avoid modifying originals and set to midnight
  const d1 = new Date(date1);
  d1.setHours(0, 0, 0, 0);

  const d2 = new Date(date2);
  d2.setHours(0, 0, 0, 0);

  // Get the difference in days using UTC to prevent timezone issues
  const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());

  // Calculate the difference in calendar days
  return Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
}

// Helper function to calculate time difference in minutes
export function getMinutesBetweenTimes(time1: string, time2: string): number {
  // Convert both times to minutes for comparison

  // Handle day format (d.HH:MM:SS) for time1
  let days1 = 0;
  let timeComponent1 = time1;

  if (time1.includes('.')) {
    const parts = time1.split('.');
    days1 = Number(parts[0]) || 0;
    timeComponent1 = parts[1] || '';
  }

  // Handle day format (d.HH:MM:SS) for time2
  let days2 = 0;
  let timeComponent2 = time2;

  if (time2.includes('.')) {
    const parts = time2.split('.');
    days2 = Number(parts[0]) || 0;
    timeComponent2 = parts[1] || '';
  }

  const timeParts1 = timeComponent1.split(':').map(Number);
  const timeParts2 = timeComponent2.split(':').map(Number);

  const hours1 = timeParts1[0] || 0;
  const minutes1 = timeParts1[1] || 0;

  const hours2 = timeParts2[0] || 0;
  const minutes2 = timeParts2[1] || 0;

  // Calculate total minutes including days
  const totalMinutes1 = days1 * 24 * 60 + hours1 * 60 + minutes1;
  const totalMinutes2 = days2 * 24 * 60 + hours2 * 60 + minutes2;

  return totalMinutes2 - totalMinutes1;
}

// Helper to format time delta in a readable format
export function formatTimeDelta(minutesDelta: number): string {
  const hours = Math.floor(Math.abs(minutesDelta) / 60);
  const minutes = Math.abs(minutesDelta) % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}

// Parse ISO date string safely
export function parseISODateString(
  dateString: string | undefined
): Date | null {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

// Add a helper function to normalize dates for comparison
// Add this before the updateLessonDate function
export const areDatesEqual = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const isDateAfterOrEqual = (date1: Date, date2: Date): boolean => {
  if (areDatesEqual(date1, date2)) return true;

  return date1 > date2;
};

export const formatDateWithOrdinal = (date: Date) => {
  const day = date.getDate();
  const suffix = ['th', 'st', 'nd', 'rd'][
    day % 10 > 3 ? 0 : (day % 100) - (day % 10) !== 10 ? day % 10 : 0
  ];
  return date
    .toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    .replace(/\d+/, day + (suffix ?? 'th'));
};

// Helper function to format time display (moved from LessonDateEditor to file level)
export function formatTimeForDisplay(time?: string | null): string {
  // Default to 10:00 AM if no time provided
  if (!time) return '10:00 AM';

  const timeString = time as string; // Type assertion

  // If already in a readable format like "10:00 AM", return as is
  if (timeString.includes(' ')) return timeString;

  try {
    // Common 24h time formats: "14:30" or "14:30:00"
    const parts = timeString.split(':');

    // Must have at least hours and minutes
    if (parts.length < 2) return timeString;

    const hours = parseInt(parts[0] || '0', 10);
    const minutes = parseInt(parts[1] || '0', 10);

    // Guard against NaN
    if (isNaN(hours) || isNaN(minutes)) return timeString;

    // Format with AM/PM
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert 0 to 12
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch {
    // Fall back to the original format if parsing fails
    return timeString;
  }
}

/**
 * Formats a time string to a standardized am/pm format
 * This is the primary function to use for displaying times consistently across the app
 *
 * @param timeString Time string in format 'HH:MM:SS', 'HH:MM', or similar
 * @param includeSeconds Whether to include seconds in the output (default: false)
 * @returns Formatted time string in format '2:30pm' or '2:30:45pm' if includeSeconds is true
 */
export function formatTimeAmPm(
  timeString?: string | null,
  includeSeconds: boolean = false
): string {
  if (!timeString) return '';

  try {
    // Ensure timeString is actually a string
    if (typeof timeString !== 'string') {
      // Non-string input - convert to string
      return String(timeString || '');
    }

    // Parse the time components
    const parts = timeString.split(':');
    const hours = parseInt(parts[0] || '0', 10);
    const minutes = parseInt(parts[1] || '0', 10);
    const seconds = parts.length > 2 ? parseInt(parts[2] || '0', 10) : 0;

    // Handle invalid parsing
    if (isNaN(hours) || isNaN(minutes)) {
      return String(timeString);
    }

    // Convert to 12-hour format
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM

    // Format the time string
    let formattedTime = `${hour12}:${minutes.toString().padStart(2, '0')}`;

    // Add seconds if requested
    if (includeSeconds) {
      formattedTime += `:${seconds.toString().padStart(2, '0')}`;
    }

    // Add period (am/pm)
    formattedTime += period;

    return formattedTime;
  } catch {
    // Error formatting time - return fallback
    return String(timeString || '');
  }
}

/**
 * Formats a date for display in a short format with day name (British/Australian style)
 * Example: "Mon, 15 May 2023"
 */
export function formatDateWithDayName(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'EEE, d MMM yyyy');
}

/**
 * Formats a date in standard British/Australian style
 * Example: "15 May 2023"
 */
export function formatDateShort(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'd MMM yyyy');
}

/**
 * Formats a week day name (short format)
 * Example: "Mon", "Tue", etc.
 */
export function formatWeekdayShort(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'EEE');
}

/**
 * Formats a week day name (long format)
 * Example: "Monday", "Tuesday", etc.
 */
export function formatWeekdayLong(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'EEEE');
}

/**
 * Formats a month name (short format)
 * Example: "Jan", "Feb", etc.
 */
export function formatMonthShort(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'MMM');
}

/**
 * Formats a date for scheduler header display (day and month only)
 * Example: "15 May"
 */
export function formatDateHeaderDisplay(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'd MMM');
}

/**
 * Formats a date with weekday prefix for stay service displays
 * Example: "Mon 15 May 2025"
 */
export function formatDateWithWeekdayPrefix(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'EEE d MMM yyyy');
}

/**
 * Formats a date with weekday prefix and ordinal suffix
 * Example: "Mon June 5th, 2025"
 */
export function formatDateWithWeekdayAndOrdinal(date: Date): string {
  const weekday = formatWeekdayShort(date);
  const day = date.getDate();
  const suffix = getOrdinalSuffix(day);
  const month = date.toLocaleDateString('en-GB', { month: 'long' });
  const year = date.getFullYear();
  return `${weekday} ${month} ${day}${suffix}, ${year}`;
}

/**
 * Format date range for display when dates are in the same month
 * Example: "15-20 May 2023" or "15 May - 20 Jun 2023"
 */
export function formatDateRange(fromDate: Date, toDate: Date): string {
  const sameMonth =
    fromDate.getMonth() === toDate.getMonth() &&
    fromDate.getFullYear() === toDate.getFullYear();

  if (sameMonth) {
    return `${format(fromDate, 'd')}-${format(toDate, 'd MMM yyyy')}`;
  }

  // Different months
  if (fromDate.getFullYear() === toDate.getFullYear()) {
    // Same year
    return `${format(fromDate, 'd MMM')} - ${format(toDate, 'd MMM yyyy')}`;
  }

  // Different years
  return `${format(fromDate, 'd MMM yyyy')} - ${format(toDate, 'd MMM yyyy')}`;
}

/**
 * Format time range for display with consistent formatting
 * Example: "10:00am - 2:00pm" or "10:00am - 2:00pm"
 * Uses en-AU locale for consistency
 */
export function formatTimeRangeShort(
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined
): string {
  // Handle case where inputs are not strings or are undefined
  const startTimeStr =
    typeof startTime === 'string'
      ? startTime
      : startTime instanceof Date
        ? format(startTime, 'HH:mm:ss')
        : '';

  const endTimeStr =
    typeof endTime === 'string'
      ? endTime
      : endTime instanceof Date
        ? format(endTime, 'HH:mm:ss')
        : '';

  // If either time is missing, return empty string
  if (!startTimeStr || !endTimeStr) {
    return '';
  }

  return `${formatTimeAmPm(startTimeStr)} - ${formatTimeAmPm(endTimeStr)}`;
}

/**
 * Standardized formatter for schedule display using British/Australian format
 * Formats both the date and time range
 * Example: "Mon, 15 May 2023, 10:00am - 2:00pm"
 */
export function formatScheduleDateTime(
  date: Date | string,
  startTime: string,
  endTime: string
): string {
  const formattedDate = formatDateWithDayName(date);
  const formattedTimeRange = formatTimeRangeShort(startTime, endTime);

  return `${formattedDate}, ${formattedTimeRange}`;
}

// Function to format currency with proper country/currency support
export function formatCurrency(
  amount: number,
  countryCodeOrInfo?: string | { countryCode: string; currencyCode: string }
): string {
  let countryCode = 'AU';
  let currencyCode = 'AUD';

  if (typeof countryCodeOrInfo === 'string') {
    // Legacy support: country code string
    countryCode = countryCodeOrInfo;
    // Map country code to currency code
    currencyCode = countryCode === 'GB' ? 'GBP' : 'AUD';
  } else if (countryCodeOrInfo) {
    // New support: CountryInfo object
    countryCode = countryCodeOrInfo.countryCode;
    currencyCode = countryCodeOrInfo.currencyCode;
  }

  return new Intl.NumberFormat(`en-${countryCode}`, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a date with time in British/Australian style with "at" between date and time
 * Example: "Mon 26 Apr 2025 at 10:46am"
 */
export function formatDateTimeWithAt(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  const formattedDate = format(dateObj, 'EEE d MMM yyyy');
  const formattedTime = formatTimeAmPm(format(dateObj, 'HH:mm:ss'));
  return `${formattedDate} at ${formattedTime}`;
}

/**
 * Extracts and formats just the time component from a Date object
 * for API usage, ensuring it's in the HH:mm:ss format
 */
export function formatTimeOnly(date: Date): string {
  return formatTimeForApi(format(date, 'HH:mm:ss'));
}

/**
 * Parses a time input string into a standardized format
 * Handles various formats like "HH:mm", "H:mm", etc.
 * @param timeInput The time string to parse
 * @param defaultValue Default value to return if parsing fails
 * @returns A standardized time string in format "HH:mm"
 */
export function parseTimeInput(
  timeInput: string | null | undefined,
  defaultValue: string = '07:00'
): string {
  // Default if no input provided
  if (!timeInput) return defaultValue;

  try {
    // Handle string type
    if (typeof timeInput === 'string') {
      // Return the complete time string in HH:mm format
      const timeParts = timeInput.split(':');
      const hour = timeParts[0]
        ? timeParts[0].padStart(2, '0')
        : defaultValue.split(':')[0];

      if (timeParts.length >= 2 && timeParts[1]) {
        const minute = timeParts[1].padStart(2, '0');
        return `${hour}:${minute}`;
      }

      return `${hour}:00`;
    }
  } catch {
    // Error parsing time input - return fallback
  }

  return defaultValue;
}

/**
 * Formats a time value (HH:mm:ss) to display just HH:mm
 * Used for cleaner display of time strings in UI components
 */
export function formatTimeWithoutSeconds(
  timeString: string | undefined | null
): string {
  if (!timeString) return '';

  try {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeString;
  } catch {
    return timeString || '';
  }
}

/**
 * Formats a date to show how long ago it was from now
 * Example: "2 hours ago", "3 days ago", etc.
 */
export function formatRelativeTime(date: string | Date): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}

/**
 * Format date range with ordinals for natural language display
 * Example: "between the 12th and 23rd of May 2025"
 */
export function formatDateRangeWithOrdinals(
  fromDate: Date,
  toDate: Date
): string {
  // Same day
  if (fromDate.toDateString() === toDate.toDateString()) {
    return `on ${formatDateWithDayName(fromDate)}`;
  }

  // Get components to build date range text
  const fromDay = fromDate.getDate();
  const toDay = toDate.getDate();
  const fromMonth = fromDate.toLocaleString('en-GB', { month: 'long' });
  const toMonth = toDate.toLocaleString('en-GB', { month: 'long' });
  const fromYear = fromDate.getFullYear();
  const toYear = toDate.getFullYear();

  // Get ordinal suffixes
  const fromSuffix = getOrdinalSuffix(fromDay);
  const toSuffix = getOrdinalSuffix(toDay);

  // Same month & year
  if (fromMonth === toMonth && fromYear === toYear) {
    return `between the ${fromDay}${fromSuffix} and ${toDay}${toSuffix} of ${fromMonth} ${fromYear}`;
  }

  // Different months, same year
  if (fromYear === toYear) {
    return `between the ${fromDay}${fromSuffix} of ${fromMonth} and the ${toDay}${toSuffix} of ${toMonth} ${fromYear}`;
  }

  // Different years
  return `between the ${fromDay}${fromSuffix} of ${fromMonth} ${fromYear} and the ${toDay}${toSuffix} of ${toMonth} ${toYear}`;
}

/**
 * Get number format based on country
 * @param countryCodeOrInfo - Country code string or CountryInfo object
 * @returns Intl.NumberFormat instance
 */
export function getNumberFormatter(
  countryCodeOrInfo?: string | { countryCode: string }
): Intl.NumberFormat {
  let countryCode = 'AU';
  if (typeof countryCodeOrInfo === 'string') {
    countryCode = countryCodeOrInfo;
  } else if (countryCodeOrInfo) {
    countryCode = countryCodeOrInfo.countryCode;
  }

  return new Intl.NumberFormat(`en-${countryCode}`);
}

/**
 * Converts empty strings and whitespace-only strings to null for optional fields.
 * This is useful for API requests where empty strings should be sent as null
 * instead of empty strings, which is common for optional database fields.
 *
 * @param value - The string value to convert, can be undefined or string
 * @returns null if value is undefined, empty string, or contains only whitespace, otherwise returns the original value
 *
 * @example
 * convertEmptyToNull("") // returns null
 * convertEmptyToNull(undefined) // returns null
 * convertEmptyToNull("hello") // returns "hello"
 * convertEmptyToNull("  ") // returns null (whitespace-only strings are now converted to null)
 * convertEmptyToNull(" hello ") // returns " hello " (strings with content are preserved)
 */
export function convertEmptyToNull(value: string | undefined): string | null {
  return value === undefined || value === '' || value.trim() === ''
    ? null
    : value;
}

/**
 * Formats a duration into a human-readable string with proper day, hour, minute breakdown
 * Handles various input formats: hours (number), HH:MM:SS (string), or minutes (number)
 * Converts large hour counts into days automatically (e.g., "177 hours" becomes "7 days, 9 hours")
 *
 * @param duration - Duration as hours (number), HH:MM:SS (string), or minutes if isMinutes=true
 * @param options - Formatting options
 * @returns Human-readable duration string
 *
 * @example
 * formatDurationReadable(177) // "7 days, 9 hours"
 * formatDurationReadable("177:15:00") // "7 days, 9 hours, 15 minutes"
 * formatDurationReadable(1890, { isMinutes: true }) // "1 day, 7 hours, 30 minutes"
 */
export function formatDurationReadable(
  duration: number | string | null | undefined,
  options: {
    isMinutes?: boolean;
    includeSeconds?: boolean;
    compact?: boolean;
    maxUnits?: number;
  } = {}
): string {
  if (duration == null || duration === '') {
    return '';
  }

  const {
    isMinutes = false,
    includeSeconds = false,
    compact = false,
    maxUnits = 3,
  } = options;

  let totalMinutes = 0;

  // Convert various input formats to total minutes
  if (typeof duration === 'number') {
    if (isMinutes) {
      totalMinutes = duration;
    } else {
      // Input is hours
      totalMinutes = duration * 60;
    }
  } else if (typeof duration === 'string') {
    // Handle HH:MM:SS or HH:MM format
    const timeMatch = duration.match(/^(\d+):(\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1] || '0', 10);
      const minutes = parseInt(timeMatch[2] || '0', 10);
      const seconds = parseInt(timeMatch[3] || '0', 10);

      totalMinutes = hours * 60 + minutes;
      if (includeSeconds && seconds > 0) {
        totalMinutes += seconds / 60;
      }
    } else {
      // Try to parse as a number (string representation)
      const numericValue = parseFloat(duration);
      if (!isNaN(numericValue)) {
        totalMinutes = isMinutes ? numericValue : numericValue * 60;
      } else {
        return duration; // Return original if we can't parse it
      }
    }
  } else {
    return '';
  }

  // Handle zero or negative durations
  if (totalMinutes <= 0) {
    return compact ? '0m' : '0 minutes';
  }

  // Convert to time units
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.round((totalMinutes * 60) % 60);

  // Build the result array
  const parts: string[] = [];

  if (days > 0) {
    if (compact) {
      parts.push(`${days}d`);
    } else {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
  }

  if (hours > 0) {
    if (compact) {
      parts.push(`${hours}h`);
    } else {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
  }

  if (minutes > 0) {
    if (compact) {
      parts.push(`${minutes}m`);
    } else {
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }
  }

  if (includeSeconds && seconds > 0) {
    if (compact) {
      parts.push(`${seconds}s`);
    } else {
      parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    }
  }

  // If no parts were added (e.g., less than 1 minute), show the most appropriate unit
  if (parts.length === 0) {
    if (totalMinutes < 1) {
      return compact ? '<1m' : 'less than 1 minute';
    } else {
      return compact ? '1m' : '1 minute';
    }
  }

  // Limit the number of units shown
  const limitedParts = parts.slice(0, maxUnits);

  // Join with appropriate separators
  if (compact) {
    return limitedParts.join(' ');
  } else {
    if (limitedParts.length === 1) {
      return limitedParts[0]!;
    } else if (limitedParts.length === 2) {
      return limitedParts.join(' and ');
    } else {
      const last = limitedParts.pop();
      return limitedParts.join(', ') + ' and ' + last;
    }
  }
}

/**
 * Formats duration specifically for timeline badges with appropriate compactness
 * Automatically handles conversion from hours to days when appropriate
 *
 * @param duration - Duration in hours (number) or HH:MM:SS format (string)
 * @param durationLabel - Optional override label to use instead
 * @returns Formatted duration for timeline display
 */
export function formatDurationForTimeline(
  duration: number | string | null | undefined,
  durationLabel?: string | null
): string {
  // Priority: use durationLabel if available and not empty
  if (durationLabel && durationLabel.trim()) {
    return durationLabel.trim();
  }

  if (duration == null || duration === '') {
    return '';
  }

  // Use the comprehensive formatter with timeline-appropriate options
  return formatDurationReadable(duration, {
    compact: true,
    maxUnits: 2, // Show max 2 units for timeline badges (e.g., "7d 9h" not "7d 9h 15m")
  });
}
